
import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePdf, useSaveShareSettings } from '../../../queries/pdfs';

export default function SharePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const pdfId = searchParams.get('id');
  const { data: pdfData = null, isLoading: loading } = usePdf(pdfId);
  const shareSettingsMutation = useSaveShareSettings();
  const [shareEnabled, setShareEnabled] = useState(false);
  const [shareToken, setShareToken] = useState('');
  const [shareLink, setShareLink] = useState('');
  const [copied, setCopied] = useState(false);
  const [sharePassword, setSharePassword] = useState('');
  const [hasExistingPassword, setHasExistingPassword] = useState(false);
  const [usePassword, setUsePassword] = useState(false);
  const [expiryOption, setExpiryOption] = useState<'none' | '1h' | '24h' | '7d' | '30d'>('none');
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState('');
  const initialTokenRef = useRef<string>('');

  useEffect(() => {
    if (!pdfId) {
      navigate('/dashboard');
      return;
    }
  }, [pdfId, navigate]);

  useEffect(() => {
    if (pdfData?.share_enabled && pdfData?.share_token) {
      setShareEnabled(true);
      setShareToken(pdfData.share_token);
      initialTokenRef.current = pdfData.share_token;
      setShareLink(`${window.location.origin}/shared/${pdfData.share_token}`);
      setUsePassword(!!pdfData.share_password);
      setHasExistingPassword(!!pdfData.share_password);
      setExpiryOption((pdfData.share_expiry as any) || 'none');
    }
  }, [pdfData]);

  const generateShareLink = () => {
    const token = crypto.randomUUID();
    setShareToken(token);
    setShareLink(`${window.location.origin}/shared/${token}`);
    setShareEnabled(true);
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = shareLink;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleSave = async () => {
    if (!pdfId) return;
    setSaving(true);
    setSaveError('');
    try {
      // 비밀번호: 새로 입력한 경우에만 전송, 기존 비밀번호 유지 시 null
      const passwordToSend = usePassword && sharePassword ? sharePassword : null;
      // 비밀번호 보호 해제 시 빈 문자열 대신 명시적으로 처리
      const shouldClearPassword = !usePassword && hasExistingPassword;

      await shareSettingsMutation.mutateAsync({
        pdfId: pdfId!,
        shareEnabled,
        shareToken: shareEnabled ? shareToken : null,
        sharePassword: shouldClearPassword ? '' : (passwordToSend ?? ''),
        shareExpiry: expiryOption,
        shareCreatedAt: shareEnabled ? new Date().toISOString() : null,
      });

      if (usePassword && sharePassword) {
        setHasExistingPassword(true);
        setSharePassword('');
      }
      if (!usePassword) {
        setHasExistingPassword(false);
      }

      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 2000);
    } catch (err) {
      console.error('저장 오류:', err);
      setSaveError(t('dashboard.share_save_error'));
      setTimeout(() => setSaveError(''), 3000);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-3 border-indigo-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 헤더 */}
      <header className="bg-white border-b border-gray-200 px-4 sm:px-8 py-4 flex items-center justify-between sticky top-0 z-10">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/dashboard')}
            className="w-9 h-9 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-lg transition-all cursor-pointer"
          >
            <i className="ri-arrow-left-line text-lg text-gray-600"></i>
          </button>
          <div>
            <h1 className="text-lg font-bold text-gray-900">{t('dashboard.share_settings')}</h1>
            <p className="text-xs text-gray-500">{pdfData?.file_name?.replace('.pdf', '')}</p>
          </div>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap disabled:opacity-50"
        >
          {saving ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
              {t('common.saving')}
            </>
          ) : saveSuccess ? (
            <>
              <i className="ri-check-line"></i>
              {t('common.save_complete')}
            </>
          ) : (
            <>
              <i className="ri-save-line"></i>
              {t('common.save')}
            </>
          )}
        </button>
      </header>

      <div className="max-w-2xl mx-auto p-4 sm:p-8">
        {/* 공유 링크 생성 */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-indigo-100 rounded-xl flex items-center justify-center">
                <i className="ri-link text-xl text-indigo-500"></i>
              </div>
              <div>
                <h2 className="text-base font-bold text-gray-900">{t('dashboard.share_link')}</h2>
                <p className="text-xs text-gray-500">{t('dashboard.share_link_desc')}</p>
              </div>
            </div>
            <button
              onClick={() => {
                if (shareEnabled) {
                  setShareEnabled(false);
                  setShareLink('');
                } else {
                  generateShareLink();
                }
              }}
              className={`relative w-12 h-7 rounded-full transition-all cursor-pointer ${
                shareEnabled ? 'bg-indigo-500' : 'bg-gray-300'
              }`}
            >
              <div className={`absolute top-0.5 w-6 h-6 bg-white rounded-full shadow-md transition-all ${
                shareEnabled ? 'left-[22px]' : 'left-0.5'
              }`}></div>
            </button>
          </div>

          {shareEnabled && shareLink && (
            <div className="space-y-4">
              {/* 링크 복사 */}
              <div className="flex items-center gap-2">
                <div className="flex-1 bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-600 truncate">
                  {shareLink}
                </div>
                <button
                  onClick={handleCopy}
                  className={`flex items-center gap-1.5 px-4 py-3 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                    copied
                      ? 'bg-emerald-500 text-white'
                      : 'bg-indigo-500 hover:bg-indigo-600 text-white'
                  }`}
                >
                  <i className={copied ? 'ri-check-line' : 'ri-file-copy-line'}></i>
                  {copied ? t('common.copied') : t('common.copy')}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* 비밀번호 설정 */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
                <i className="ri-lock-line text-xl text-amber-500"></i>
              </div>
              <div>
                <h2 className="text-base font-bold text-gray-900">{t('dashboard.password_protection')}</h2>
                <p className="text-xs text-gray-500">{t('dashboard.password_protection_desc')}</p>
              </div>
            </div>
            <button
              onClick={() => setUsePassword(!usePassword)}
              className={`relative w-12 h-7 rounded-full transition-all cursor-pointer ${
                usePassword ? 'bg-indigo-500' : 'bg-gray-300'
              }`}
            >
              <div className={`absolute top-0.5 w-6 h-6 bg-white rounded-full shadow-md transition-all ${
                usePassword ? 'left-[22px]' : 'left-0.5'
              }`}></div>
            </button>
          </div>

          {usePassword && (
            <div className="space-y-2">
              <input
                type="text"
                value={sharePassword}
                onChange={(e) => setSharePassword(e.target.value)}
                placeholder={hasExistingPassword ? t('dashboard.new_password_placeholder') : t('dashboard.enter_password_placeholder')}
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
              {hasExistingPassword && !sharePassword && (
                <p className="text-xs text-gray-400">{t('dashboard.existing_password_hint')}</p>
              )}
            </div>
          )}
        </div>

        {/* 만료 기간 설정 */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
              <i className="ri-time-line text-xl text-emerald-500"></i>
            </div>
            <div>
              <h2 className="text-base font-bold text-gray-900">{t('dashboard.expiry')}</h2>
              <p className="text-xs text-gray-500">{t('dashboard.expiry_desc')}</p>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
            {[
              { value: 'none' as const, label: t('dashboard.expiry_none') },
              { value: '1h' as const, label: t('dashboard.expiry_1h') },
              { value: '24h' as const, label: t('dashboard.expiry_24h') },
              { value: '7d' as const, label: t('dashboard.expiry_7d') },
              { value: '30d' as const, label: t('dashboard.expiry_30d') },
            ].map((option) => (
              <button
                key={option.value}
                onClick={() => setExpiryOption(option.value)}
                className={`px-3 py-2.5 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                  expiryOption === option.value
                    ? 'bg-indigo-500 text-white shadow-lg'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        {/* 에러 메시지 */}
        {saveError && (
          <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-xl text-sm text-red-600">
            {saveError}
          </div>
        )}
      </div>
    </div>
  );
}
